import React, { useState } from 'react';
import { List } from '@consta/uikit/ListCanary';
import { Card } from '@consta/uikit/Card';
import { Button } from '@consta/uikit/Button';
import { Badge } from '@consta/uikit/Badge';
import { Text } from '@consta/uikit/Text';
import { IconInfo } from '@consta/icons/IconInfo';
import { IconEdit } from '@consta/icons/IconEdit';
import { IconCopy } from '@consta/icons/IconCopy';
import { IconAdd } from '@consta/icons/IconAdd';
import { IconArrowUp } from '@consta/icons/IconArrowUp';
import { IconArrowDown } from '@consta/icons/IconArrowDown';
import { IconTrash } from '@consta/icons/IconTrash/index';
import { useCreateUnnamedMutation } from '../../../../services/areaService';

interface FieldListProps {
    fields: any[];
    totalCount: number;
    isLoading: boolean;
    onToggleVisibility: (fieldId: string, type: 'deposits' | 'wells') => void;
    onInfoClick: (field: any) => void;
    onEditField: (field: any) => void;
    onAddDeposit: (fieldId: string) => void;
    onEditDeposit: (fieldId: string, deposit: any) => void;
    onAddSection: (fieldId: string, depositId: string) => void;
    onEditSection: (fieldId: string, depositId: string, section: any) => void;
    onAddHorizon: (fieldId: string, depositId: string, sectionId: string) => void;
    onEditHorizon: (fieldId: string, depositId: string, sectionId: string, horizon: any) => void;
    onAddBed: (fieldId: string, depositId: string, sectionId: string, horizonId: string) => void;
    onEditBed: (fieldId: string, depositId: string, sectionId: string, horizonId: string, bed: any) => void;
    onToggleDepositVisibility: (fieldId: string, depositId: string) => void;
    onToggleSectionVisibility: (fieldId: string, depositId: string, sectionId: string) => void;
    onToggleHorizonVisibility: (fieldId: string, depositId: string, sectionId: string, horizonId: string) => void;
    onDuplicateDeposit: (fieldId: string, depositUuid: string, deposit: any) => void;
    onDuplicateSection: (fieldId: string, depositId: string, areaUuid: string, section: any) => void;
    onDuplicateHorizon: (fieldId: string, depositId: string, sectionId: string, horizonUuid: string, horizon: any) => void;
    onDuplicateBed: (fieldId: string, depositId: string, sectionId: string, horizonId: string, layerUuid: string, bed: any) => void;
    onDeleteField: (fieldUuid: string, field: any) => void;
    onDeleteDeposit: (fieldUuid: string, depositUuid: string, deposit: any) => void;
    onDeleteSection: (fieldUuid: string, depositUuid: string, areaUuid: string, section: any) => void;
    onDeleteHorizon: (fieldUuid: string, depositUuid: string, areaUuid: string, horizonUuid: string, horizon: any) => void;
    onDeleteBed: (fieldUuid: string, depositUuid: string, areaUuid: string, horizonUuid: string, layerUuid: string, bed: any) => void;
    onAreaAdded?: () => void;
}

const FieldList: React.FC<FieldListProps> = ({
    fields,
    totalCount,
    isLoading,
    onToggleVisibility,
    onInfoClick,
    onEditField,
    onAddDeposit,
    onEditDeposit,
    onDuplicateDeposit,
    onAddSection,
    onEditSection,
    onDuplicateSection,
    onAddHorizon,
    onEditHorizon,
    onDuplicateHorizon,
    onAddBed,
    onEditBed,
    onDuplicateBed,
    onToggleDepositVisibility,
    onToggleSectionVisibility,
    onToggleHorizonVisibility,
    onDeleteField,
    onDeleteDeposit,
    onDeleteSection,
    onDeleteHorizon,
    onDeleteBed,
    onAreaAdded
}) => {
    // Только состояния видимости
    const [fieldsVisibility, setFieldsVisibility] = useState<{[key: string]: boolean}>({});
    const [depositsVisibility, setDepositsVisibility] = useState<{[key: string]: boolean}>({});
    const [areasVisibility, setAreasVisibility] = useState<{[key: string]: boolean}>({});
    const [horizonsVisibility, setHorizonsVisibility] = useState<{[key: string]: boolean}>({});
    const [postUnnamed] = useCreateUnnamedMutation()

    const handleAddEmptyArea = async (depositUuid: string) => {
        try {
            await postUnnamed({ deposit: depositUuid }).unwrap();
            if (onAreaAdded) {
                onAreaAdded();
            }
        } catch (error) {
            console.error('Ошибка при добавлении пустого участка:', error);
        }
    };

    // Обработчики раскрытия/скрытия
    const handleToggleField = (fieldUuid: string) => {
        setFieldsVisibility(prev => ({
            ...prev,
            [fieldUuid]: !prev[fieldUuid]
        }));
        onToggleVisibility(fieldUuid, 'deposits');
    };

    const handleToggleDeposit = (fieldUuid: string, depositUuid: string) => {
        setDepositsVisibility(prev => ({
            ...prev,
            [depositUuid]: !prev[depositUuid]
        }));
        onToggleDepositVisibility(fieldUuid, depositUuid);
    };

    const handleToggleArea = (fieldUuid: string, depositUuid: string, areaUuid: string) => {
        setAreasVisibility(prev => ({
            ...prev,
            [areaUuid]: !prev[areaUuid]
        }));
        onToggleSectionVisibility(fieldUuid, depositUuid, areaUuid);
    };

    const handleToggleHorizon = (fieldUuid: string, depositUuid: string, areaUuid: string, horizonUuid: string) => {
        setHorizonsVisibility(prev => ({
            ...prev,
            [horizonUuid]: !prev[horizonUuid]
        }));
        onToggleHorizonVisibility(fieldUuid, depositUuid, areaUuid, horizonUuid);
    };

    // Проверка видимости
    const isFieldVisible = (fieldUuid: string) => fieldsVisibility[fieldUuid] || false;
    const isDepositVisible = (depositUuid: string) => depositsVisibility[depositUuid] || false;
    const isAreaVisible = (areaUuid: string) => areasVisibility[areaUuid] || false;
    const isHorizonVisible = (horizonUuid: string) => horizonsVisibility[horizonUuid] || false;

    // Подсчет количества элементов для бейджей
    const getDepositCount = (field: any) => field.deposits?.length || 0;
    const getAreaCount = (deposit: any) => deposit.areas?.length || 0;
    const getHorizonCount = (area: any) => area.horizons?.length || 0;
    const getLayerCount = (horizon: any) => horizon.layers?.length || 0;

    const renderDeposits = (field: any) => {
        const deposits = field.deposits || [];
        
        return (
            <div style={{ marginLeft: 20, marginTop: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <Text size="m" weight="semibold">Залежи ({deposits.length})</Text>
                    <Button
                        size="xs"
                        iconLeft={IconAdd}
                        label="Добавить залежь"
                        onClick={() => onAddDeposit(field.uuid)}
                    />
                </div>
                {deposits.map((deposit: any) => (
                    <Card key={deposit.uuid} verticalSpace="s" horizontalSpace="s" style={{ marginBottom: 10 }} className="card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Button
                                    iconLeft={isDepositVisible(deposit.uuid) ? IconArrowUp : IconArrowDown}
                                    onlyIcon
                                    view="clear"
                                    size="xs"
                                    onClick={() => handleToggleDeposit(field.uuid, deposit.uuid)}
                                />
                                <Text weight="semibold">{deposit.name}</Text>
                                <Badge 
                                    size="s" 
                                    status="normal" 
                                    label={`Участков: ${getAreaCount(deposit)}`} 
                                    style={{ marginLeft: 8 }} 
                                />
                            </div>
                            <div style={{ display: 'flex', gap: 4 }}>
                                <Button
                                    iconLeft={IconEdit}
                                    size='xs'
                                    view='ghost'
                                    onClick={() => onEditDeposit(field.uuid, deposit)}
                                />
                                <Button
                                    iconLeft={IconCopy}
                                    size='xs'
                                    view='ghost'
                                    onClick={() => onDuplicateDeposit(field.uuid, deposit.uuid, deposit)}
                                />
                                <Button
                                    iconLeft={IconTrash}
                                    size='xs'
                                    view='ghost'
                                    onClick={() => onDeleteDeposit(field.uuid, deposit.uuid, deposit)}
                                />
                            </div>
                        </div>

                        {isDepositVisible(deposit.uuid) && renderAreas(field.uuid, deposit)}
                    </Card>
                ))}
            </div>
        );
    };

    const renderAreas = (fieldUuid: string, deposit: any) => {
        const areas = deposit.areas || [];
        
        return (
            <div style={{ marginLeft: 20, marginTop: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <Text size="s" weight="semibold">Участки ({areas.length})</Text>
                    <div style={{display: 'flex', flexDirection: 'row', gap: '12px'}}>
                        <Button
                            size="xs"
                            iconLeft={IconAdd}
                            label="Добавить пустой участок"
                            onClick={() => handleAddEmptyArea(deposit.uuid)}
                        />
                        <Button
                            size="xs"
                            iconLeft={IconAdd}
                            label="Добавить участок"
                            onClick={() => onAddSection(fieldUuid, deposit.uuid)}
                        />
                    </div>
                </div>
                {areas.map((area: any) => (
                    <Card key={area.uuid} verticalSpace="s" horizontalSpace="s" style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Button
                                    iconLeft={isAreaVisible(area.uuid) ? IconArrowUp : IconArrowDown}
                                    onlyIcon
                                    view="clear"
                                    size="xs"
                                    onClick={() => handleToggleArea(fieldUuid, deposit.uuid, area.uuid)}
                                />
                                <Text size="s" weight="semibold">{area.name}</Text>
                                <Badge 
                                    size="s" 
                                    status="system" 
                                    label={`Горизонтов: ${getHorizonCount(area)}`} 
                                    style={{ marginLeft: 8 }} 
                                />
                            </div>
                            <div style={{ display: 'flex', gap: 4 }}>
                                <Button
                                    iconLeft={IconEdit}
                                    size='xs'
                                    view='ghost'
                                    onClick={() => onEditSection(fieldUuid, deposit.uuid, area)}
                                />
                                <Button
                                    iconLeft={IconCopy}
                                    size='xs'
                                    view='ghost'
                                    onClick={() => onDuplicateSection(fieldUuid, deposit.uuid, area.uuid, area)}
                                />
                                <Button
                                    iconLeft={IconTrash}
                                    size='xs'
                                    view='ghost'
                                    onClick={() => onDeleteSection(fieldUuid, deposit.uuid, area.uuid, area)}
                                />
                            </div>
                        </div>

                        {isAreaVisible(area.uuid) && renderHorizons(fieldUuid, deposit.uuid, area)}
                    </Card>
                ))}
            </div>
        );
    };

    const renderHorizons = (fieldUuid: string, depositUuid: string, area: any) => {
        const horizons = area.horizons || [];
        
        return (
            <div style={{ marginLeft: 20, marginTop: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <Text size="xs" weight="semibold">Горизонты ({horizons.length})</Text>
                    <Button
                        size="xs"
                        iconLeft={IconAdd}
                        label="Добавить горизонт"
                        onClick={() => onAddHorizon(fieldUuid, depositUuid, area.uuid)}
                    />
                </div>
                {horizons.map((horizon: any) => (
                    <div key={horizon.uuid} style={{ margin: 10, padding: 8, borderRadius: 4, boxShadow: 'var(--shadow-group)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Button
                                    iconLeft={isHorizonVisible(horizon.uuid) ? IconArrowUp : IconArrowDown}
                                    onlyIcon
                                    view="clear"
                                    size="xs"
                                    onClick={() => handleToggleHorizon(fieldUuid, depositUuid, area.uuid, horizon.uuid)}
                                />
                                <Text size="xs" weight="semibold">{horizon.name}</Text>
                                <Badge 
                                    size="s" 
                                    status="warning" 
                                    label={`Пластов: ${getLayerCount(horizon)}`} 
                                    style={{ marginLeft: 6 }} 
                                />
                            </div>
                            <div style={{ display: 'flex', gap: 4 }}>
                                <Button
                                    iconLeft={IconEdit}
                                    size='xs'
                                    view='ghost'
                                    onClick={() => onEditHorizon(fieldUuid, depositUuid, area.uuid, horizon)}
                                />
                                <Button
                                    iconLeft={IconCopy}
                                    size='xs'
                                    view='ghost'
                                    onClick={() => onDuplicateHorizon(fieldUuid, depositUuid, area.uuid, horizon.uuid, horizon)}
                                />
                                <Button
                                    iconLeft={IconTrash}
                                    size='xs'
                                    view='ghost'
                                    onClick={() => onDeleteHorizon(fieldUuid, depositUuid, area.uuid, horizon.uuid, horizon)}
                                />
                            </div>
                        </div>

                        {isHorizonVisible(horizon.uuid) && renderLayers(fieldUuid, depositUuid, area.uuid, horizon)}
                    </div>
                ))}
            </div>
        );
    };

    const renderLayers = (fieldUuid: string, depositUuid: string, areaUuid: string, horizon: any) => {
        const layers = horizon.layers || [];
        
        return (
            <div style={{ marginLeft: 20, marginTop: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <Text size="xs" weight="semibold">Пласты ({layers.length})</Text>
                    <Button
                        size="xs"
                        iconLeft={IconAdd}
                        label="Добавить пласт"
                        onClick={() => onAddBed(fieldUuid, depositUuid, areaUuid, horizon.uuid)}
                    />
                </div>
                {layers.map((layer: any) => (
                    <div key={layer.uuid} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 0' }}>
                        <Text size="xs">{layer.name}</Text>
                        <div style={{ display: 'flex', gap: 4 }}>
                            <Button
                                iconLeft={IconEdit}
                                size='xs'
                                view='ghost'
                                onClick={() => onEditBed(fieldUuid, depositUuid, areaUuid, horizon.uuid, layer)}
                            />
                            <Button
                                iconLeft={IconCopy}
                                size='xs'
                                view='ghost'
                                onClick={() => onDuplicateBed(fieldUuid, depositUuid, areaUuid, horizon.uuid, layer.uuid, layer)}
                            />
                             <Button
                                iconLeft={IconTrash}
                                size='xs'
                                view='ghost'
                                onClick={() => onDeleteBed(fieldUuid, depositUuid, areaUuid, horizon.uuid, layer.uuid, layer)}
                            />
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                <Text>Загрузка месторождений...</Text>
            </div>
        );
    }

    if (!fields.length && !isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                <Text>Месторождения не найдены</Text>
            </div>
        );
    }

    return (
        <div>
            <Text size="s" view="secondary" style={{ marginBottom: 16 }}>
                Найдено месторождений: {totalCount}
            </Text>
            {/*@ts-ignore*/}
            <List
                items={fields}
                //@ts-ignore
                getItemKey={(item: any) => item?.uuid}
                renderItem={(field: any) => (
                    <Card verticalSpace="m" horizontalSpace="m" style={{ marginBottom: 16 }} className="card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Button
                                    iconLeft={isFieldVisible(field.uuid) ? IconArrowUp : IconArrowDown}
                                    onlyIcon
                                    view="clear"
                                    onClick={() => handleToggleField(field.uuid)}
                                />
                                <strong style={{ marginLeft: 8 }}>{field.name}</strong>
                                <Badge
                                    size="m"
                                    status="success"
                                    label={`Залежей: ${getDepositCount(field)}`}
                                    style={{ marginLeft: 12 }}
                                />
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <Button
                                    iconLeft={IconInfo}
                                    size='xs'
                                    view='ghost'
                                    onClick={() => onInfoClick(field)}
                                />
                                <Button
                                    iconLeft={IconEdit}
                                    size='xs'
                                    view='ghost'
                                    onClick={() => onEditField(field)}
                                />
                                <Button
                                    iconLeft={IconTrash}
                                    size='xs'
                                    view='ghost'
                                    onClick={() => onDeleteField(field.uuid, field)}
                                />
                            </div>
                        </div>

                        {/* Залежи */}
                        {isFieldVisible(field.uuid) && renderDeposits(field)}
                    </Card>
                )}
            />
        </div>
    );
};

export default FieldList;